import { warn } from '@ember/debug';
import Serializer from '@ember-data/serializer';
import { decorateClass, isDecoratorArgs } from 'decorator-setup';

/**
 * Deletes relationships that are not included in response.
 * This prevents ember-data from attempting to fetch the missing relationships.
 * @example
 * \@includeOnlyRelationships
 * class MyClass { }
 * @example
 * \@includeOnlyRelationships('items', 'states')
 * class MyClass { }
 * @decorator
 */
export function includeOnlyRelationships(...allowedRelationships) {
  const isFactory = !isDecoratorArgs(...arguments);
  if (!isFactory) {
    allowedRelationships = [];
  }
  function classDecorator(Target) {
    if (!Serializer.isPrototypeOf(Target)) {
      throw new Error(
        '@includeOnlyRelationships decorator should be applied to an Ember Serializer derived class',
      );
    }
    return decorateClass(class IncludeOnlyRelationshipsDecoratedSerializer extends Target {
      normalizeResponse(store, primaryModelClass, payload, id, requestType) {
        payload.data && [].concat(payload.data).forEach(data => {
          let { relationships } = data;
          if ( relationships && ( relationships = Object.entries(relationships) ).length ) {
            const includes = {};
            [].concat(payload.included || []).forEach(include => {
              includes[include.type] = includes[include.type] || {};
              includes[include.type][include.id] = include;
            });
            relationships.forEach(([ relType, rels ]) => {
              if (allowedRelationships.includes(relType)
                && rels.data
              ) {
                const isSingle = !Array.isArray(rels.data);
                rels.data = [].concat(rels.data).filter(rel => {
                  let included = true;
                  if (!includes[rel.type] || !includes[rel.type][rel.id]) {
                    included = false;
                    warn(
                      `Serializer for '${primaryModelClass.modelName}' deleted missing relationship with id '${rel.id}' from '${relType}'`,
                      false,
                      { id: `include-only_${rel.type}_${rel.id}` }
                    );
                  }
                  return included;
                });
                if (isSingle) {
                  if (rels.data.length) {
                    rels.data = rels.data[0];
                  } else {
                    delete data.relationships[relType];
                  }
                }
              }
            });
          }
        });
        return super.normalizeResponse(...arguments);
      }
    });
  }
  return isFactory ? classDecorator : classDecorator(...arguments);
}
